/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, ModalBody, ModalContent, ModalHeader } from '@nextui-org/react';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import { PaymentService } from '../../services';
import { IconPaypal, IconMobilePayment } from '../../assets/icons';
import { classNames, Constants, formatAmount } from '../../utils';
import Render from '../../components/render';
import MobilePayment from './mobile-payment';
import moment from 'moment';

const AcquirePlan = ({ initPayment, onSuccess, onClose, plan, user }) => {
  const { countryId, form, onSubmit, paymentMethods, payWithMobile, setPayWithMobile } = useForm(initPayment, plan, user);

  const submit = async (paymentMethodId) => {
    const response = await onSubmit(paymentMethodId);
    if (response) onSuccess(response);
  }

  const planDetails = useMemo(() => {
    const isFree = plan.price === 0;
    const PD = isFree ? Constants.PLAN_DURATION.FREE : Constants.PLAN_DURATION.PAID;

    return {
      isFree,
      expiration: moment().add(PD.AMOUNT, PD.UNIT).locale('es').format('DD [de] MMMM [de] YYYY'),
    };
  }, [plan]);

  return (
    <>
      <Render condition={payWithMobile}>
        <MobilePayment
          type={Constants.PAYMENT_TYPE.ACQUIRE}
          request={form}
          onClose={() => setPayWithMobile(false)}
          onSuccess={onSuccess}
        />
      </Render>

      <Modal size="md" isOpen={true} onClose={onClose}>
        <ModalContent>
          <ModalHeader className="flex flex-col">Pagar Plan</ModalHeader>
          <ModalBody className="relative">
            <div className="mb-4">
              <p className="capitalize font-semibold text-lg text-secondary">{ plan?.name }</p>
              <p className="text-sm text-red font-semibold">Fecha de Vencimiento</p>
              <p>{ planDetails.expiration }</p>
            </div>

            <div className="flex items-center justify-between h-fit text-secondary mb-0 bg-[#EBF8FF] px-2 py-2 rounded-md">
              <p className="font-semibold text-lg">Monto</p>
              <p className="font-semibold text-lg">{ formatAmount(form.amount, '$') }</p>
            </div>

            <p className="text-center font-semibold">Seleccione el método de pago</p>

            <Render condition={countryId === Constants.COUNTRIES.COL}>
              <div className="grid grid-cols-2 gap-4 my-4">
                <Render condition={paymentMethods.some(m => m.id === Constants.PAYMENT_METHODS.CO_PAYPAL)}>
                  <PaymentButton
                    label="PayPal"
                    icon={IconPaypal}
                    className="bg-primary text-white"
                    onSubmit={() => submit(Constants.PAYMENT_METHODS.CO_PAYPAL)}
                  />
                </Render>

                <Render condition={paymentMethods.some(m => m.id === Constants.PAYMENT_METHODS.CO_MOBILE)}>
                  <PaymentButton
                    label="Pago Móvil"
                    icon={IconMobilePayment}
                    onSubmit={() => submit(Constants.PAYMENT_METHODS.CO_MOBILE)}
                  />
                </Render>
              </div>
            </Render>

            <Render condition={countryId === Constants.COUNTRIES.VEN}>
              <div className="grid grid-cols-2 gap-4 my-4">
                <Render condition={paymentMethods.some(m => m.id === Constants.PAYMENT_METHODS.VE_PAYPAL)}>
                  <PaymentButton
                    label="PayPal"
                    icon={IconPaypal}
                    className="bg-primary text-white"
                    onSubmit={() => submit(Constants.PAYMENT_METHODS.VE_PAYPAL)}
                  />
                </Render>

                <Render condition={paymentMethods.some(m => m.id === Constants.PAYMENT_METHODS.VE_MOBILE)}>
                  <PaymentButton
                    label="Pago Móvil"
                    icon={IconMobilePayment}
                    onSubmit={() => submit(Constants.PAYMENT_METHODS.VE_MOBILE)}
                  />
                </Render>
              </div>
            </Render>

          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

const PaymentButton = ({ label, icon, className, isDisabled, onSubmit }) => (
  <Button
    className={classNames('flex flex-1 justify-center items-center gap-2 border-primary pl-1', className)}
    variant="bordered"
    radius="sm"
    onPress={onSubmit}
    isDisabled={isDisabled}
  >
    <div className="bg-white p-[2px] rounded-sm flex items-center justify-cente">
      <img src={icon} alt="paypal" className="w-6 h-6" />
    </div>
    <p className="flex-1 font-semibold">{ label }</p>
  </Button>
)

const useForm = (initPayment, plan, user) => {
  const [form, setForm] = useState({
    plan_id: plan.id,
    amount: plan.price,
    plan,
    user,
  });
  const [countryId, setCountryId] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [payWithMobile, setPayWithMobile] = useState(false);
  const location = useLocation();

  const getPaymentMethods = () => {
    PaymentService.payer.getMethods({ user_id: user.id })
      .then(res => {
        setCountryId(res[0].country_id);
        setPaymentMethods(res);
      })
      .catch(console.log);
  }

  const onChange = (value, target) => {
    setForm(s => ({ ...s, [target]: value }));
  }

  const submit = async (paymentMethodId) => {
    try {
      const mobilePayment = [Constants.PAYMENT_METHODS.CO_MOBILE, Constants.PAYMENT_METHODS.VE_MOBILE];
      const payPalPayment = [Constants.PAYMENT_METHODS.CO_PAYPAL, Constants.PAYMENT_METHODS.VE_PAYPAL];

      if (payPalPayment.includes(paymentMethodId)) {
        await payWithPayPal();

      } else if (mobilePayment.includes(paymentMethodId)) {
        setPayWithMobile(true);

      } else {
        return toast.error('Debe seleccionar un método de pago válido');
      }

    } catch (error) {
      toast.error(error || 'Ocurrió un error inesperado');
    }
  }

  const payWithPayPal = async () => {
    try {
      await initPayment(user, form.plan_id, form.amount, location.pathname);

    } catch (error) {
      toast.error(error || 'Ocurrió un error inesperado');
    }
  }

  useEffect(() => {
    getPaymentMethods();
  }, []);

  return {
    countryId,
    form,
    onChange,
    onSubmit: submit,
    paymentMethods,
    payWithMobile,
    setPayWithMobile,
  }
}

export default AcquirePlan;
